/**
 * Personal Data Encryption Utilities
 * Client-side encryption utilities for sensitive personal data
 * Uses master key encryption with AES-256-GCM
 */

import {
  encryptDataWithMasterKey,
  decryptDataWithMasterKey,
} from '@xanectech/encryption';
import { createLogger } from '@xanectech/lib';
import {
  getCachedMasterKey,
  isSessionInitialized,
  ensureSessionUnlocked,
} from '@xanectech/encryption';

const logger = createLogger('Data - encryption');

interface EncryptedPayload {
  encryptedData: string;
  iv: string;
}

const isEncryptedPayload = (value: unknown): value is EncryptedPayload => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const payload = value as {
    encryptedData?: unknown;
    iv?: unknown;
  };

  return (
    typeof payload.encryptedData === 'string' && typeof payload.iv === 'string'
  );
};

/**
 * Encrypt a value using master key encryption
 * Uses AES-256-GCM with the user's master key
 */
export async function encryptValue(value: string): Promise<string> {
  if (!value || typeof value !== 'string') {
    throw new Error('Value must be a non-empty string');
  }

  try {
    // Get current user from auth context
    const { supabase } = await import('@xanectech/supabase-client');
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    // Ensure session is unlocked (restores from IndexedDB if needed)
    const sessionReady = await ensureSessionUnlocked(user.id);
    if (!sessionReady) {
      throw new Error(
        'Encryption session not initialized. Please unlock your encryption or log in again.',
      );
    }

    // Get cached master key from session
    const masterKey = getCachedMasterKey(user.id);

    if (!masterKey) {
      throw new Error(
        'Encryption session not initialized. Please unlock your encryption or log in again.',
      );
    }

    // Encrypt the value using master key (note: first param is masterKey, second is data)
    const encrypted = await encryptDataWithMasterKey(masterKey, value);

    // Return as JSON string for storage in database (format: { encryptedData, iv })
    return JSON.stringify(encrypted);
  } catch (error) {
    logger.error('Failed to encrypt personal data field:', {
      error: error instanceof Error ? error.message : String(error),
    });
    throw new Error(
      `Encryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
}

/**
 * Decrypt a value using master key decryption
 * Uses AES-256-GCM with the user's master key
 */
export async function decryptValue(encryptedValue: string): Promise<string> {
  if (!encryptedValue || typeof encryptedValue !== 'string') {
    return '';
  }

  try {
    // Get current user from auth context
    const { supabase } = await import('@xanectech/supabase-client');
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    // Parse encrypted data from JSON
    let parsedData: unknown;
    try {
      parsedData = JSON.parse(encryptedValue);
    } catch (parseError) {
      // Handle legacy base64 encoded data (from placeholder implementation)
      logger.warn('Attempting to decrypt legacy placeholder data');
      try {
        return atob(encryptedValue);
      } catch {
        throw new Error('Invalid encrypted data format');
      }
    }

    // Ensure session is unlocked (restores from IndexedDB if needed)
    const sessionReady = await ensureSessionUnlocked(user.id);
    if (!sessionReady) {
      throw new Error(
        'Encryption session not initialized. Please unlock your encryption or log in again.',
      );
    }

    // Get cached master key from session
    const masterKey = getCachedMasterKey(user.id);

    if (!masterKey) {
      throw new Error(
        'Encryption session not initialized. Please unlock your encryption or log in again.',
      );
    }

    // Decrypt using master key (note: params are masterKey, encryptedData, iv)
    if (!isEncryptedPayload(parsedData)) {
      throw new Error(
        'Invalid encrypted data format: missing or invalid encryptedData or iv',
      );
    }

    const { encryptedData, iv } = parsedData;

    const decrypted = await decryptDataWithMasterKey(
      masterKey,
      encryptedData,
      iv,
    );
    return decrypted;
  } catch (error) {
    logger.error('Failed to decrypt personal data field:', {
      error: error instanceof Error ? error.message : String(error),
    });

    // Provide user-friendly error messages
    if (error instanceof Error) {
      if (error.message.includes('Session locked')) {
        throw error; // Pass through session lock errors
      }
      if (
        error.message.includes('decryption') ||
        error.message.includes('decrypt')
      ) {
        throw new Error(
          'Unable to decrypt data. Please verify your encryption password.',
        );
      }
    }

    throw new Error(
      `Decryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
}

/**
 * Determine if a field should be encrypted based on its properties
 *
 * SECURITY FIX: All user data fields should be encrypted by default,
 * regardless of is_sensitive/is_pii flags. Only structural layout fields
 * should skip encryption.
 */
export function shouldEncryptField(field: {
  is_sensitive: boolean;
  is_pii: boolean;
  data_type?: string;
}): boolean {
  // Encrypt ALL personal data fields by default
  const alwaysEncryptTypes = [
    'text',
    'email',
    'phone',
    'textarea',
    'address',
    'url',
    'date',
    'datetime',
    'number',
    'currency',
    'password',
    'richtext',
    'file',
    'json',
    'select',
    'multiselect',
    'country',
    'province',
    'city',
    'language',
    'boolean',
    'time',
    'radio',
    'checkbox',
    'percentage',
    'range',
  ];

  // Only skip encryption for purely structural/metadata fields
  const skipEncryptionTypes = [
    'spacer',
    'divider',
    'field_group',
    'tab_container',
    'dialog_button',
  ];

  if (field.data_type && skipEncryptionTypes.includes(field.data_type)) {
    return false;
  }

  // For fields with explicit data_type, check if it's in encrypt list
  if (field.data_type && alwaysEncryptTypes.includes(field.data_type)) {
    return true;
  }

  // Default to encrypt everything else (including legacy sensitive/PII logic)
  return true;
}

/**
 * Generate a secure hash for searchable indexing
 * Uses SHA-256 for non-PII fields to enable search without decryption
 */
export async function hashValue(value: string): Promise<string> {
  if (!value || typeof value !== 'string') {
    return '';
  }

  try {
    // Normalize the value for consistent hashing
    const normalized = value.toLowerCase().trim();

    // Use Web Crypto API for secure hashing
    const encoder = new TextEncoder();
    const data = encoder.encode(normalized);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Convert to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    return hashHex;
  } catch (error) {
    logger.error('Failed to hash value:', error as Error);
    throw new Error(
      `Hashing failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
}

/**
 * Migration utility to detect and re-encrypt placeholder data
 * This should be run once during the upgrade to ensure all existing data is properly encrypted
 */
export async function migrateFromPlaceholderEncryption(
  placeholderEncryptedValue: string,
): Promise<{ encrypted: string; hash: string } | null> {
  if (!placeholderEncryptedValue) return null;

  try {
    // Check if this looks like placeholder data (base64 without JSON structure)
    const isPlaceholder =
      !placeholderEncryptedValue.startsWith('{') &&
      placeholderEncryptedValue.length > 0 &&
      /^[A-Za-z0-9+/]*={0,2}$/.test(placeholderEncryptedValue);

    if (!isPlaceholder) {
      // Already properly encrypted
      return null;
    }

    // Decode placeholder data
    const originalValue = atob(placeholderEncryptedValue);

    // Re-encrypt with proper encryption
    const encrypted = await encryptValue(originalValue);
    const hash = await hashValue(originalValue);

    logger.info('✅ Migrated placeholder encrypted data to proper encryption');

    return { encrypted, hash };
  } catch (error) {
    logger.error('Failed to migrate placeholder data:', error as Error);
    // Don't throw - let the system handle this gracefully
    return null;
  }
}

/**
 * Utility to check if session is properly unlocked for encryption operations
 */
export async function checkEncryptionSession(): Promise<{
  isUnlocked: boolean;
  userId: string | null;
  error?: string;
}> {
  try {
    const { supabase } = await import('@xanectech/supabase-client');
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
      return { isUnlocked: false, userId: null, error: 'Not authenticated' };
    }

    const hasMasterKey = isSessionInitialized(user.id);

    return {
      isUnlocked: hasMasterKey,
      userId: user.id,
      error: hasMasterKey
        ? undefined
        : 'Session locked - master key not available',
    };
  } catch (error) {
    return {
      isUnlocked: false,
      userId: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
